import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, filter, fromEvent, debounceTime } from 'rxjs';
export type AppRole = 'admin' | 'manager' | 'user';

const STORAGE_KEY = 'dmp-sidebar-collapsed';
const MOBILE_BREAKPOINT = 1024;

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly _sidebarOpen = new BehaviorSubject<boolean>(false);
  private readonly _collapsed = new BehaviorSubject<boolean>(this.loadCollapsed());
  private readonly _isMobile = new BehaviorSubject<boolean>(this.readIsMobile());

  /** Mobile overlay drawer open */
  readonly sidebarOpen$ = this._sidebarOpen.asObservable();
  /** Desktop collapsed (icon-only) sidebar */
  readonly sidebarCollapsed$ = this._collapsed.asObservable();
  readonly isMobile$ = this._isMobile.asObservable();

  constructor(private router: Router) {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this._sidebarOpen.next(false));

    if (isPlatformBrowser(this.platformId)) {
      this.handleResize();
      fromEvent(window, 'resize')
        .pipe(debounceTime(150))
        .subscribe(() => this.handleResize());
    }
  }

  get isCollapsed(): boolean {
    return this._collapsed.value;
  }

  get isMobile(): boolean {
    return this._isMobile.value;
  }

  /** Mobile drawer toggle (hamburger) */
  toggleSidebar(): void {
    this._sidebarOpen.next(!this._sidebarOpen.value);
  }

  closeSidebar(): void {
    this._sidebarOpen.next(false);
  }

  /** Desktop collapse toggle (chevrons) */
  toggleCollapse(): void {
    const next = !this._collapsed.value;
    this._collapsed.next(next);
    this.persistCollapsed(next);
  }

  private handleResize(): void {
    const mobile = this.readIsMobile();
    const wasMobile = this._isMobile.value;
    this._isMobile.next(mobile);

    if (mobile) {
      this._sidebarOpen.next(false);
      if (!wasMobile) {
        this._collapsed.next(true);
      }
    } else if (wasMobile) {
      this._collapsed.next(this.loadCollapsed());
    }
  }

  private loadCollapsed(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    return localStorage.getItem(STORAGE_KEY) === 'true';
  }

  private persistCollapsed(collapsed: boolean): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  }

  private readIsMobile(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }
}
